import React, { useState } from 'react';
import { FaChevronDown, FaChevronRight, FaCodeBranch, FaServer, FaMobile, FaDesktop, FaHandshake } from 'react-icons/fa';

const releases = [
  {
    version: 'April 2026',
    date: 'April 1-2, 2026',
    highlight: 'Major coupon system overhaul, Pull API fixes, Admin Panel enhancements',
    changes: [
      { repo: 'Backend', items: [
        'Fix Pull API batch process: cron jobs were self-deleting after first run — now persist and repeat',
        'Fix case-sensitivity bug in bulkRedeemCoupons — universal coupons now process all users in a batch',
        'Fix coupon visibility: Rewards page now hides products when all slots (assigned + redeemed) are taken',
        'Implement coupon expiry with point refund — assigned coupons freed after timeout, points returned to user',
        'Add coupon_expired_refund notification type to notification service',
        'Fix campaign reactivation: coupons now re-enabled when campaign toggled back to Active',
        'Fix CouponRedemptionHook crash: checkRedemptionPercentage safe with optional chaining',
        'Fix bulkRedeemCoupons upsert: preserve campaignId, productId, brandId on new records',
        'Add userId validation to assignCouponToUser to prevent CastError crashes',
        'Disable auto-timers per client request — coupon expiry only via Admin Panel buttons',
      ]},
      { repo: 'Admin Panel', items: [
        'Add "Unique" to Coupon Code Format dropdown for Promote Brand/Product campaigns',
        'Add "Run Pull API" manual button on User Coupons page (blue)',
        'Add "Free Expired Coupons" manual button on User Coupons page (orange)',
        'Add detailed error messages in toast popups for batch process failures',
        'Rename config key to coupon_reassignment_limit_minutes for clarity',
        'Add Driver.js interactive Page Guide for all 88+ pages',
        'Add comprehensive User Guide page with full documentation',
        'Add "Page Guide" help button in header bar',
      ]},
      { repo: 'Mobile App', items: [
        'Fix project date display: read eventStartDateTime with fallback for Plant Tree campaigns',
        'Fix Rewards page: assigned coupons now stay visible so user can re-open coupon popup',
        'Add 72-hour expiry footnote to coupon popup',
        'Fix Report Content/Report User: bold red buttons, added to Blog/EcoActivity/Tips comment popups',
        'Fix Community Guidelines: in-app page with client\'s exact text instead of website redirect',
        'Remove "raise funds" and "meal donations" text from walkthrough slides',
      ]},
      { repo: 'Partner Panel', items: [
        'Remove non-working Change Password from Settings & Profile page',
        'Remove non-working Edit (pencil) icon from campaign rows',
      ]},
    ]
  },
  {
    version: 'March 2026 (Late)',
    date: 'March 28-31, 2026',
    highlight: 'Google OAuth fix, NoTracking coupons, challenge-service crash fixes',
    changes: [
      { repo: 'Backend', items: [
        'Fix Google login: accept all OAuth client IDs (new app + beta + legacy) as valid audience',
        'Use environment variables for Google OAuth Client IDs with Firebase defaults',
        'Feature #206: Unique coupon with No Tracking for Product Promotion (assignment = redemption)',
        'Fix Bug #184: App not fetching coupon from Sponsored Challenge — link campaignId at join time',
        'Fix challenge-service crash: duplicate voucherData variable causing SyntaxError',
        'Fix Agenda.js bulkRedeemCoupons error handling with try-catch wrapper',
        'Add profanity filter on comment creation',
        'Fix null safety crash in campaignImpactCalculator',
        'Block inactive campaign products/coupons from app visibility',
        'Fix partner panel impact API — pass through all coupon fields',
      ]},
      { repo: 'Mobile App', items: [
        'Update google-services.json with new Firebase project (my-cc-app-39a95)',
        'New package com.climatecrusaders.app for Play Store submission',
        'Enforce 18+ age requirement in registration',
        'Add Community Guidelines page and Report Content/Report User links',
        'Handle NoTracking coupon in marketplace popup (hide Redeem button, show OK)',
        'Automate API URL switching between sandbox and production via react-native-config',
      ]},
    ]
  },
  {
    version: 'March 2026 (Mid)',
    date: 'March 14-22, 2026',
    highlight: 'Pull API flow, coupon redemption fixes, campaign edit UX',
    changes: [
      { repo: 'Backend', items: [
        'Fix CS-403 coupon redemption bug when multiple campaigns share same uniqueId',
        'Remove MongoDB transaction from redeemCoupon for standalone DB compatibility',
        'Unify Sponsor campaign impact popup with Promote Brand/Product format',
        'Fix Clear functionality: delete notifications and invites properly',
        'Fix Pull API and coupon redemption URL issues',
        'Fix View Your Reward: resolve campaignId from challenge when missing',
        'Fix campaign fields not retained during edit',
      ]},
      { repo: 'Admin Panel', items: [
        'Auto-populate webhook URL and link when WebHook redemption type selected',
        'Fix Product/Services dropdown not showing saved values on campaign edit',
        'Fix Pull API campaign form: auto-populate endpoint and validate headers',
        'Fix coupon redemption: fetch productId dynamically',
        'Fix auth-key in CouponRedemption to match production ACCESS_TOKEN_KEY',
        'Filter product/services dropdown by client when brand is not selected',
      ]},
    ]
  },
  {
    version: 'March 2026 (Early)',
    date: 'March 1-11, 2026',
    highlight: 'Google Sign-In fix, quiz fix, blog image fix',
    changes: [
      { repo: 'Backend', items: [
        'Fix Google Sign-In: accept all OAuth client IDs as valid audience',
        'Fixed quiz creation production issue (#199)',
        'Merge development: all Feb 27 release fixes + Google Sign-In client ID update',
      ]},
      { repo: 'Admin Panel', items: [
        'Fixed blog images not getting saved (#200)',
        'Fixed wrong event date shown in Plant Tree campaign (#191)',
        'KPI values selected in tree campaign restoring after update fix',
      ]},
    ]
  },
  {
    version: 'February 2026',
    date: 'February 2026',
    highlight: 'Blog fixes, time/date bugs, tips module, new company creation, pipeline setup',
    changes: [
      { repo: 'Backend', items: [
        'Fixed eco activities listing (14 items) (#189)',
        'Fixed new company creation issue',
        'Pipeline setup for production deployment',
      ]},
      { repo: 'Admin Panel', items: [
        'Blog issue fixes (images, content)',
        'Email multiple recipients feature',
        'Fixed time/date display bugs',
        'Tips module bug fixes',
        'Knowledge board error handling',
        'Campaign bugs #161 fixed',
        'Fixed users dropdown in assign badge to users',
        'Base URL fixes for sandbox/production',
        'Azure Pipelines CI/CD setup',
      ]},
      { repo: 'Partner Panel', items: [
        'Fix dashboard metrics showing 0 — read from totalImpactMetrics nested object',
        'Azure Pipelines CI/CD setup',
      ]},
    ]
  },
  {
    version: 'January 2026',
    date: 'January 2026',
    highlight: 'Performance fixes, pagination, No Tracking option, webhook URL, Plant Tree auto-deactivate',
    changes: [
      { repo: 'Backend', items: [
        'MASSIVE PERFORMANCE: Fix Admin Panel App User hanging issue',
        'Add pagination to NGO and Company list APIs',
        'Fix event auto-deactivation and active field logic',
        'Fix certificate S3 URL expiration and add tree plantation certificates',
        'CRITICAL: Integrate URL regeneration in API endpoints',
        'Add auto-deactivate for Plant Tree campaigns at max certificates',
        'Add webhookUrl field to Campaign model',
        'Fix inactive campaign visibility in marketplace coupons',
        'Add auto-deactivate campaign when all coupons exhausted',
        'Fix critical coupon assignment and availability bugs',
        'Fix Challenge of the Day participation counting',
        'Add product click tracking and enhance notification system',
        'Fix sponsored challenge description not showing on Home page',
        'Fix bugs: Ambassador impact metrics, password validation, share app links',
      ]},
      { repo: 'Admin Panel', items: [
        'Add No Tracking option to Redemption Type dropdown',
        'Add webhook URL field to campaign form',
        'Make Product/Service optional for brand-only campaigns',
        'Fix campaign fields resetting to null during edit',
        'Add product click metrics to campaign details',
        'Fix ambassador metrics display',
        'Add pagination to Ambassador, NGO, Company, and App User lists',
        'Filter events dropdown to show only active events',
      ]},
    ]
  },
  {
    version: 'December 2025',
    date: 'December 2025',
    highlight: 'NGO/Company logo fixes, notification fixes, gateway setup, mobile app major fixes',
    changes: [
      { repo: 'Backend', items: [
        'Fix NGO and Company logo upload issues',
        'Fix OTP queuing system',
        'Fixed notifications flow',
        'Event changes and city field added',
        'Challenge of the day fixes',
        'Gateway service setup and routing',
        'CORS headers fix for proxied responses',
        'Remove auth requirement from GET image endpoint',
      ]},
      { repo: 'Admin Panel', items: [
        'Fix Eco Activity module',
        'NGO and Company form fixes',
        'Base URL changes for different environments',
        'Campaign creation fixes',
      ]},
      { repo: 'Mobile App', items: [
        'Major fixes across the mobile app',
        'Hero section pull-to-refresh fix',
        'Login screen and flow updates',
        'Challenge of the day and cool-off period fix',
        'Fix refresh functionality',
        'Azure CI/CD pipeline setup',
      ]},
    ]
  },
  {
    version: 'November 2025',
    date: 'November 2025',
    highlight: 'User module fixes, proposed challenges, referral codes, notification system, mobile app major changes',
    changes: [
      { repo: 'Backend', items: [
        'Notification system implementation and templates',
        'User deletion flow and fixes',
        'Blog submission handling',
        'Calculate carbon savings in real-time',
        'Facebook login and user account deletion',
        'Email template creation',
        'Backend API fixes across services',
        'Challenge list and submission fixes',
      ]},
      { repo: 'Admin Panel', items: [
        'User module UI fixes as per test document',
        'Proposed challenges module',
        'Added referral codes',
        'Bug fixing for user management',
        'Fixed pagination across modules',
        'Eco Activity Organizer',
      ]},
      { repo: 'Mobile App', items: [
        'Major changes across the app',
        'Demographics and profile page changes',
        'Badge system changes',
        'Fixes across entire app (URLs, navigation, data)',
        'Dynamic points display',
        'Login screen and flow improvements',
        'CI/CD pipeline setup for Azure',
      ]},
    ]
  },
  {
    version: 'October 2025',
    date: 'October 2025',
    highlight: 'Pagination, eco activity, carbon savings, mobile login stabilization',
    changes: [
      { repo: 'Backend', items: [
        'Partner notifications implementation',
        'Face verification toggle in challenge submission',
        'KPI tracking updates',
        'Coupon data for campaign impact',
        'Partner forgot password flow',
        'Image upload and file naming fixes',
        'Magic link email changes',
        'Registration email fixes',
        'Carbon savings in Eco Activities',
        'Azure Pipelines CI/CD setup',
        'Ad controller fixes',
        'Fixed authentication and templates',
      ]},
      { repo: 'Admin Panel', items: [
        'Fixed pagination across modules',
        'Eco Activity Organizer feature',
        'Added carbon savings field in verification',
      ]},
      { repo: 'Mobile App', items: [
        'Stabilized login and registration modules',
        'Carbon footprint tracking setup',
        'OTP length and validation fixes',
        'Major app fixes and stability improvements',
        'Azure CI/CD pipeline setup with Gradle caching',
        'Build pipeline optimizations',
      ]},
    ]
  },
  {
    version: 'September 2025',
    date: 'September 2025',
    highlight: 'Ad module, AI validation, dashboard, Plant Trees, coupon system, notifications',
    changes: [
      { repo: 'Backend', items: [
        'Dashboard APIs implementation',
        'Tree plantation campaign and certificates',
        'Plant Trees KPI tracking',
        'Partner dashboard API updates',
        'S3 migration for media storage',
        'AI face validation for submissions',
        'Ad service: new microservice for advertisements',
        'Click and impression KPI tracking',
        'Ad slot allocation APIs',
        'Complete notification system: challenge, blog, referral, admin, bulk notifications',
        'Challenge proposal scheduler with voting',
        'Coupon redemption and tracking APIs',
        'Campaign impact API updates',
        'Product promotion campaign backend',
        'Points deduction for coupon redemption',
        'Blog notification flow (approved, rejected, liked)',
        'Support ticket notifications',
        'Gateway routing for new ad-service',
      ]},
      { repo: 'Admin Panel', items: [
        'Dashboard with metrics and charts',
        'Advertisement module (CRUD, slots, allocations)',
        'AI image validation for submissions',
        'Ad clicks and impressions tracking',
        'Badge module updates',
        'Campaign impact details modal',
        'Promote Brand/Product campaign complete',
        'Plant Trees KPI and certificates',
        'Company form updates',
      ]},
      { repo: 'Mobile App', items: [
        'Plant Trees campaign and donation flow',
        'Community empty states',
        'Advertisement display in challenge homepage',
        'Click tracking and impressions',
        'Community configs',
        'Badge updates',
        'Ambassador details page fixes',
        'Marketplace update for brand promotions',
        'iOS version updates',
      ]},
    ]
  },
  {
    version: 'August 2025',
    date: 'August 2025',
    highlight: 'Complete coupon system, Pull API, webhook, document repo, notifications, invites',
    changes: [
      { repo: 'Backend', items: [
        'Complete coupon module: creation, assignment, tracking, redemption',
        'Coupon webhook hook APIs',
        'Pull API implementation with Agenda.js scheduler',
        'Mongo Agenda for scheduled jobs (coupon expiry, campaign deactivation)',
        'Coupon activity logging system',
        'Campaign coupon reactivation when campaign reactivated',
        'Temporary Pull API data storage',
        'User challenge coupon linking',
        'Password generation for new users',
        'Invite module microservice',
        'User notifications with pagination',
        'Admin notification routes',
        'SQS message queue integration',
        'Document service setup',
        'Brand module CRUD',
        'Ambassador profile management',
        'Referral points allocation',
        'Clear invites functionality',
        'Company and NGO creation updates',
        'User deletion management',
        'Sponsored challenge fixes',
        'CI/CD pipeline setup with Azure',
      ]},
      { repo: 'Admin Panel', items: [
        'Pull API test page',
        'Webhook test page',
        'Document repository module',
        'Ambassador profile management',
        'Campaign management major updates',
        'App configs module',
        'Home page organizer with invite settings',
        'User deletion management',
        'User impact details popup',
        'Admin notification list',
        'Filters update across modules',
        'Media upload component',
        'CI/CD pipeline setup',
      ]},
      { repo: 'Mobile App', items: [
        'Coupon assignment and redemption flow',
        'Coupon popup with close button and link redirect',
        'Pull API flow integration',
        'Coupon redemption URL handling',
        'Ambassador profile updates',
        'Invite messages and clear functionality',
        'Minor fixes across the app',
        'CI/CD pipeline setup',
      ]},
    ]
  },
  {
    version: 'June-July 2025',
    date: 'June-July 2025',
    highlight: 'Campaign system, help desk, product CRUD, partner service, notifications',
    changes: [
      { repo: 'Backend', items: [
        'Campaign type: Promote Brand/Product creation APIs',
        'Rewards and coupon listing APIs',
        'Challenge campaign linking',
        'Voucher CRUD operations',
        'Product/Service controller APIs',
        'Help desk: conversation, issue resolution, chat APIs',
        'Badge and Challenge of the Day APIs',
        'Common point allocation function',
        'Rewards page display config',
        'Partner dashboard setup',
        'Partner auth and settings',
        'Partner notification system',
        'Admin and bulk-user notification',
        'Notification templates',
        'Company logo and image service',
        'Mail sending feature',
        'App hero section APIs',
        'Partner requests APIs',
        'Swagger documentation updates',
      ]},
      { repo: 'Admin Panel', items: [
        'Campaign Management with all campaign types',
        'Campaign UI dynamic by type',
        'Help desk integration (issues, inquiries)',
        'Product/Service section CRUD',
        'Badge filters and management',
        'Partner requests and notifications',
        'App hero section',
        'Rewards homepage organizer',
        'Campaign event linking',
      ]},
    ]
  },
  {
    version: 'May 2025',
    date: 'May 2025',
    highlight: 'Events, blogs, quiz, knowledge board, eco activities, comments system',
    changes: [
      { repo: 'Backend', items: [
        'Events module: create, categories, submissions, approval',
        'Blog system: CRUD, comments, likes, admin/user blogs',
        'Quiz module: creation, submission, auto-points',
        'Knowledge board: tips, videos, news CRUD with comments',
        'Eco-activity module: CRUD, comments, likes',
        'Facts/Did You Know module',
        'Comment model and APIs (unified for blogs, tips, activities)',
        'App rating/review APIs',
        'Homepage organizer APIs',
        'Challenge of the day APIs',
        'Event invite for friends',
        'Invite points system',
        'User referral system',
        'Latest and recommended challenges APIs',
        'Challenge submission verification and approval',
        'Event organizer and sponsor linking',
      ]},
      { repo: 'Admin Panel', items: [
        'Events module (create, list, categories, submissions)',
        'Blog system (create, list, edit, comments)',
        'Quiz module',
        'Homepage organizer',
        'Eco-activity module (list, details, edit)',
        'Knowledge board (tips, videos, news)',
        'Facts module',
        'Challenge submission verification updates',
        'Badges module',
        'Event submission approvals',
      ]},
    ]
  },
  {
    version: 'March-April 2025',
    date: 'March-April 2025',
    highlight: 'Challenge creation, submission verification, proposed challenges',
    changes: [
      { repo: 'Admin Panel', items: [
        'Challenge creation with multi-tab form (info, submission, tips)',
        'Challenge submission verification and points allocation',
        'Proposed challenges module (user suggestions)',
        'Challenge save draft functionality',
        'Carbon fields added to submissions',
        'Bonus points calculations',
        'MCQ question type',
        'Invite friend for a challenge',
        'Comments system updates',
      ]},
    ]
  },
  {
    version: 'February 2025',
    date: 'February 2025',
    highlight: 'Challenge system build-out, user management, tips',
    changes: [
      { repo: 'Admin Panel', items: [
        'Challenge creation with tabs, tips, and validations',
        'Challenge submission verification page',
        'Challenge list with filters',
        'Reward details and app controls',
        'User list classification (App Users, Ambassadors, NGOs, Companies)',
        'Dashboard page',
        'Recurrent duration settings',
      ]},
    ]
  },
  {
    version: 'November 2024 - January 2025',
    date: 'Nov 2024 - Jan 2025',
    highlight: 'Foundation: Auth, Users, Challenges, Products, Media Library, Database',
    changes: [
      { repo: 'Backend', items: [
        'Initial microservices architecture: auth-service, user-service, challenge-service, image-service, gateway',
        'Authentication: login, register, Google Sign-In, Apple Sign-In, Email Magic Link',
        'User model with full demographics, referral codes, and social links',
        'Challenge CRUD: creation, categories, submissions, enrollment',
        'Challenge submission with photo uploads to AWS S3',
        'User impact area tracking',
        'Help desk: issue model, contact us',
        'API documentation with Swagger',
        'Admin login with forgot password and OTP',
        'Image upload to S3 bucket',
        'Gateway service for API routing',
      ]},
      { repo: 'Admin Panel', items: [
        'Initial project structure with React and Tailwind',
        'Authentication flow (login, forgot password, OTP)',
        'User CRUD screens (create, view, edit)',
        'Challenge and category CRUD',
        'Product/Service tab screens',
        'Media Library',
        'Sidebar navigation and UI',
        'Dashboard page foundation',
      ]},
      { repo: 'Database', items: [
        'MongoDB Atlas setup for sandbox',
        'Self-hosted MongoDB on Lightsail for production',
        'Core collections: users, challenges, userChallenges, submissions, categories',
        'Indexes for performance on user queries and challenge lookups',
      ]},
    ]
  },
  {
    version: 'August - October 2024',
    date: 'Aug - Oct 2024',
    highlight: 'Project inception: Backend architecture, Mobile app foundation, AWS infrastructure',
    changes: [
      { repo: 'Backend', items: [
        'Node.js/Express microservices architecture design',
        'Auth service: JWT authentication, validation schemas',
        'User service: complete profile management',
        'Challenge service: basic setup with routes',
        'Image service: S3 bucket integration',
        'API Gateway: request routing and middleware',
        'AWS S3 bucket setup for media storage',
      ]},
      { repo: 'Mobile App', items: [
        'React Native 0.75.2 project initialization',
        'Navigation structure with 6 bottom tabs',
        'Authentication screens: login, register, onboarding',
        'Home screen with challenge cards',
        'Challenge details and submission flow',
        'Community feed with eco-activities',
        'Impact dashboard',
        'Rewards/marketplace screen',
        'Inspire section with leaderboard',
        'Settings and profile management',
        'Google Sign-In and Apple Sign-In integration',
      ]},
      { repo: 'Infrastructure', items: [
        'AWS Lightsail: 4 instances provisioned (CC_APP_BACKEND, CC_APP_TEST, ProdDB, AdminDB)',
        'PM2 process management for 12 Node.js microservices',
        'AWS S3: 6 buckets for media, app builds, and static assets',
        'AWS SQS FIFO queue for notification processing',
        'AWS CloudFront: 5 distributions for admin, partner, and media CDN',
        'MongoDB self-hosted on ProdDB instance',
        'MongoDB Atlas for sandbox/test environment',
        'SSL certificates and domain setup',
        'Nginx reverse proxy configuration',
      ]},
    ]
  },
];

const repoIcons = {
  'Backend': FaServer,
  'Admin Panel': FaDesktop,
  'Mobile App': FaMobile,
  'Partner Panel': FaHandshake,
};

const repoColors = {
  'Backend': '#e3f2fd',
  'Admin Panel': '#e8f5e9',
  'Mobile App': '#fff3e0',
  'Partner Panel': '#f3e5f5',
};

const repoTextColors = {
  'Backend': '#1565c0',
  'Admin Panel': '#2e7d32',
  'Mobile App': '#e65100',
  'Partner Panel': '#7b1fa2',
};

const ReleaseNotes = () => {
  const [expanded, setExpanded] = useState({ 'April 2026': true });
  const toggle = (v) => setExpanded(prev => ({ ...prev, [v]: !prev[v] }));

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ padding: '28px 0 20px', borderBottom: '2px solid #e8f5e9' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#151b17', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaCodeBranch style={{ color: '#15af53' }} />
          Release Notes
        </h1>
        <p style={{ fontSize: '14px', color: '#7a9a84', margin: '6px 0 0', lineHeight: '1.6' }}>
          Complete changelog across all repositories — Admin Panel, Backend Services, Mobile App, and Partner Panel.
          Spanning from project inception (November 2024) to current.
        </p>
      </div>

      <div style={{ padding: '16px 0 60px' }}>
        {releases.map(release => (
          <div key={release.version} style={{ marginBottom: '8px' }}>
            <button onClick={() => toggle(release.version)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
              padding: '16px 18px', background: expanded[release.version] ? '#e8f5e9' : '#f8faf9',
              border: '1px solid #e0e0e0', borderRadius: expanded[release.version] ? '12px 12px 0 0' : '12px',
              cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
            }}>
              {expanded[release.version] ? <FaChevronDown style={{ fontSize: '12px', color: '#15af53' }} /> : <FaChevronRight style={{ fontSize: '12px', color: '#999' }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#151b17' }}>{release.version}</div>
                <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{release.date}</div>
              </div>
              <span style={{ fontSize: '11px', color: '#7a9a84', background: '#fff', padding: '4px 12px', borderRadius: '12px', border: '1px solid #eee', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {release.highlight}
              </span>
            </button>

            {expanded[release.version] && (
              <div style={{ border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '16px 18px' }}>
                {release.changes.map(change => {
                  const Icon = repoIcons[change.repo] || FaCodeBranch;
                  return (
                    <div key={change.repo} style={{ marginBottom: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '6px', background: repoColors[change.repo] || '#f5f5f5', color: repoTextColors[change.repo] || '#333', fontSize: '12px', fontWeight: '700' }}>
                          <Icon style={{ fontSize: '11px' }} /> {change.repo}
                        </span>
                        <span style={{ fontSize: '11px', color: '#999' }}>{change.items.length} change{change.items.length > 1 ? 's' : ''}</span>
                      </div>
                      <ul style={{ margin: '0', padding: '0 0 0 20px', listStyle: 'none' }}>
                        {change.items.map((item, i) => (
                          <li key={i} style={{ fontSize: '12px', color: '#4a6b52', lineHeight: '1.7', padding: '2px 0', position: 'relative', paddingLeft: '14px' }}>
                            <span style={{ position: 'absolute', left: 0, color: '#15af53' }}>&#8226;</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReleaseNotes;
